import React, { useEffect, useState } from 'react';
import { useNavigate } from "react-router-dom";
import axios from 'axios';
import ProgressTimeline from '../components/StudentWelcome/ProgressTimeline';
import ProfilePreview from '../components/StudentWelcome/ProfilePreview';
import ProfileCompletion from '../components/StudentWelcome/ProfileCompletion';
import ApplySection from '../components/StudentWelcome/ApplySection';
import InfoCards from '../components/StudentWelcome/InfoCards';
import ProfileIncompleteModal from '../components/StudentWelcome/ProfileIncompleteModal';
import styles from '../components/StudentWelcome/StudentWelcome.module.css';
import logger from '../utils/logger';

export default function StudentWelcome() {
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState(new Set());
  const [userProfile, setUserProfile] = useState(null);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const Navigate = useNavigate();

  const userData = JSON.parse(localStorage.getItem('user'));
  const userId = userData?.id;

  const calculateProgress = async () => {
    try {
      logger.log('Fetching profile data...');
      
      // Fetch profile data
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/users/${userId}`, {
        headers: { Authorization: `Bearer ${userData.token}` }
      });
      const profile = res.data;
      logger.log('Profile data loaded successfully');
      setUserProfile(profile);

      // Get previously completed steps from the database (locked in)
      const lockedInSteps = new Set(profile.completedProgressSteps || []);
      const completed = new Set(lockedInSteps);

      // Step 1: Basic Information
      const hasBasicInfo = profile.name?.trim() &&
                          profile.education[0]?.institution?.trim() &&
                          profile.education[0]?.department?.trim();
      if (hasBasicInfo) {
        completed.add(0);
      }

      // Step 2: Research Interests
      const hasInterests = profile.interests?.length > 0;
      if (hasInterests) {
        completed.add(1);
      }

      // Step 3: Resume
      const hasResume = !!profile.resumeKey?.trim();
      if (hasResume) {
        completed.add(2);
      }

      // Only fetch API data if steps are not already locked in
      const needSavedCheck = !lockedInSteps.has(3);
      const needMatchesCheck = !lockedInSteps.has(4);

      // Run API calls in parallel if needed
      const apiPromises = [];
      if (needSavedCheck) {
        apiPromises.push(
          axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/saved/${userId}`)
            .then(savedRes => ({ type: 'saved', data: savedRes.data }))
            .catch(err => {
              logger.error('Error fetching saved positions:', err);
              return { type: 'saved', data: { savedItems: [] } };
            })
        );
      }
      
      if (needMatchesCheck) {
        apiPromises.push(
          axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/users/${userId}/matches`, {
            headers: { Authorization: `Bearer ${userData.token}` }
          })
            .then(matchesRes => ({ type: 'matches', data: matchesRes.data }))
            .catch(err => {
              logger.error('Error fetching matches:', err);
              return { type: 'matches', data: { matches: [] } };
            })
        );
      }

      // Wait for all API calls in parallel
      const results = await Promise.all(apiPromises);
      
      // Process results
      let positionsCompleted = false;
      let matchesCompleted = false;
      
      if (lockedInSteps.has(3)) {
        completed.add(3);
        positionsCompleted = true;
      } else {
        const savedResult = results.find(r => r.type === 'saved');
        if (savedResult && savedResult.data.savedItems?.length >= 3) {
          completed.add(3);
          positionsCompleted = true;
        }
      }

      if (lockedInSteps.has(4)) {
        completed.add(4);
        matchesCompleted = true;
      } else {
        const matchesResult = results.find(r => r.type === 'matches');
        if (matchesResult && matchesResult.data.matches?.length > 0) {
          completed.add(4);
          matchesCompleted = true;
        }
      }

      // Update locked-in steps if new completions occurred
      if ((positionsCompleted && !lockedInSteps.has(3)) || (matchesCompleted && !lockedInSteps.has(4))) {
        const newCompletedSteps = [];
        completed.forEach(step => newCompletedSteps.push(step));
        
        await axios.put(
          `${process.env.REACT_APP_BACKEND_URL}/api/users/${userId}`,
          { completedProgressSteps: newCompletedSteps },
          { headers: { Authorization: `Bearer ${userData.token}` } }
        );
      }

      let currentStepIndex = 0;
      for (let i = 0; i < 6; i++) {
        if (completed.has(i)) {
          currentStepIndex = i + 1;
        } else {
          break;
        }
      }

      if (completed.size === 5) {
        currentStepIndex = 5;
      }

      setCompletedSteps(completed);
      setCurrentStep(currentStepIndex);

      const isProfileComplete = completed.size >= 3;

      // Return the completion status
      return { isProfileComplete, profileCompleted: profile.profileCompleted };
    } catch (err) {
      logger.error('Error calculating progress:', err);
      return { isProfileComplete: false, profileCompleted: false };
    }
  };

  useEffect(() => {
    if (userId) {
      let isMounted = true;
      
      const loadData = async () => {
        const result = await calculateProgress();
        
        // Only update state if component is still mounted
        if (!isMounted) return;
        
        // Set modal visibility based on actual completion status
        if (result.profileCompleted === true) {
          setShowProfileModal(false);
        } else if (!result.isProfileComplete) {
          setShowProfileModal(true);
        }
      };
      
      loadData();
      
      return () => {
        isMounted = false;
      };
    }
  }, [userId, Navigate]);

  return (
    <div className={styles.studentWelcomeContainer}>
      <div className={styles.welcomeHeader}>
        <h1 className={styles.welcomeTitle}>Student Portal</h1>
        <ProgressTimeline
          currentStep={currentStep}
          completedSteps={completedSteps}
        />
      </div>

      <ProfilePreview userData={userData} userProfile={userProfile} />

      <ProfileCompletion
        completedSteps={completedSteps}
        navigateToProfile={() => Navigate('/profile')}
      />

      <ApplySection
        completedSteps={completedSteps}
        navigateToDashboard={() => Navigate('/student-dashboard')}
      />

      <InfoCards />

      {showProfileModal && userProfile && (
        <ProfileIncompleteModal
          onNavigateToProfile={() => {
            setShowProfileModal(false);
            Navigate('/profile');
          }}
          onClose={() => {
            setShowProfileModal(false);
          }}
          userData={userProfile}
          formData={{
            name: userProfile.name,
            interests: userProfile.interests || [],
            resumeKey: userProfile.resumeKey,
            firstName: userProfile.firstName,
            lastName: userProfile.lastName
          }}
          educationEntries={userProfile.education || []}
        />
      )}
    </div>
  );
}